import { lazy } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AppShell } from '@/components/layout/app-shell'
import { ProtectedRoute } from '@/components/auth/protected-route'
import { PermissionGate } from '@/components/auth/permission-gate'
import { RootErrorBoundary } from '@/components/root-error-boundary'
import { PERMISSIONS, REPORTS_HUB_PERMISSIONS } from '@/lib/permissions'
import { EXPENSE_FUND_PERMISSIONS } from '@/features/expenses/expense-utils'
import { PIECEWORK_MODULE_PERMISSIONS } from '@/features/piecework/piecework-utils'

const DashboardPage = lazy(() => import('@/pages/dashboard'))
const OrdersPage = lazy(() => import('@/pages/orders'))
const OrderNewPage = lazy(() => import('@/pages/order-new'))
const OrderEditPage = lazy(() => import('@/pages/order-edit'))
const OrderDetailPage = lazy(() => import('@/pages/order-detail'))
const CustomersPage = lazy(() => import('@/pages/customers'))
const CustomerDetailPage = lazy(() => import('@/pages/customer-detail'))
const ProductsPage = lazy(() => import('@/pages/products'))
const ProductNewPage = lazy(() => import('@/pages/product-new'))
const ProductEditPage = lazy(() => import('@/pages/product-edit'))
const ProductKeywordsPage = lazy(() => import('@/pages/product-keywords'))
const MaterialsPage = lazy(() => import('@/pages/materials'))
const MaterialEditPage = lazy(() => import('@/pages/material-edit'))
const PurchasesPage = lazy(() => import('@/pages/purchases'))
const PurchaseNewPage = lazy(() => import('@/pages/purchase-new'))
const PurchaseDetailPage = lazy(() => import('@/pages/purchase-detail'))
const SuppliersPage = lazy(() => import('@/pages/suppliers'))
const WarehouseLayout = lazy(() => import('@/pages/warehouse/warehouse-layout'))
const WhMaterialsPage = lazy(() => import('@/pages/warehouse/wh-materials-page'))
const WhHistoryPage = lazy(() => import('@/pages/warehouse/wh-history-page'))
const WhStockInHistoryPage = lazy(() => import('@/pages/warehouse/wh-stock-in-history-page'))
const WhOptimizePage = lazy(() => import('@/pages/warehouse/wh-optimize-page'))
const WhCutReportPage = lazy(() => import('@/pages/warehouse/wh-cut-report-page'))
const BtpItemsPage = lazy(() => import('@/pages/btp/btp-items-page'))
const BtpProducePage = lazy(() => import('@/pages/btp/btp-produce-page'))
const BtpReportPage = lazy(() => import('@/pages/btp/btp-report-page'))
const BtpProductionsPage = lazy(() => import('@/pages/btp/btp-productions-page'))
const BtpPrebuiltListPage = lazy(() => import('@/pages/btp/btp-prebuilt-list-page'))
const BtpPrebuiltDetailPage = lazy(() => import('@/pages/btp/btp-prebuilt-detail-page'))
const BtpPrebuiltFormPage = lazy(() => import('@/pages/btp/btp-prebuilt-form-page'))
const WhMaterialDetailPage = lazy(() => import('@/pages/warehouse/wh-material-detail-page'))
const WhBatchDetailPage = lazy(() => import('@/pages/warehouse/wh-batch-detail-page'))
const FinancePage = lazy(() => import('@/pages/finance'))
const ExpensesLayout = lazy(() => import('@/pages/expenses/expenses-layout'))
const ExpensesListPage = lazy(() => import('@/pages/expenses/expenses-list-page'))
const ExpensesInboxPage = lazy(() => import('@/pages/expenses/expenses-inbox-page'))
const ExpensesReportPage = lazy(() => import('@/pages/expenses/expenses-report-page'))
const ExpensesFlowPage = lazy(() => import('@/pages/expenses/expenses-flow-page'))
const ExpensesTypesPage = lazy(() => import('@/pages/expenses/expenses-types-page'))
const ExpenseCreatePage = lazy(() => import('@/pages/expenses/expense-create-page'))
const ExpenseDetailPage = lazy(() => import('@/pages/expenses/expense-detail-page'))
const ExpenseEditPage = lazy(() => import('@/pages/expenses/expense-edit-page'))
const ExpenseVoucherPrintPage = lazy(() => import('@/pages/print/expense-voucher'))
const ExpenseFilePage = lazy(() => import('@/pages/expenses/expense-file-page'))
const ExpensesOverviewPage = lazy(() => import('@/pages/expenses/expenses-overview-page'))
const ExpensesOrgPage = lazy(() => import('@/pages/expenses/expenses-org-page'))
const ExpensesAuditPage = lazy(() => import('@/pages/expenses/expenses-audit-page'))
const ExpenseReportPrintPage = lazy(() => import('@/pages/print/expense-report'))
const DepositsListPage = lazy(() => import('@/pages/expenses/deposits/deposits-list-page'))
const DepositCreatePage = lazy(() => import('@/pages/expenses/deposits/deposit-create-page'))
const DepositDetailPage = lazy(() => import('@/pages/expenses/deposits/deposit-detail-page'))
const CodReductionsLayout = lazy(() => import('@/pages/cod-reductions/cod-reductions-layout'))
const CodReductionsListPage = lazy(() => import('@/pages/cod-reductions/cod-reductions-list-page'))
const CodReductionsReportPage = lazy(() => import('@/pages/cod-reductions/cod-reductions-report-page'))
const CodReductionsFlowPage = lazy(() => import('@/pages/cod-reductions/cod-reductions-flow-page'))
const CodReductionsReasonsPage = lazy(() => import('@/pages/cod-reductions/cod-reductions-reasons-page'))
const CodReductionFormPage = lazy(() => import('@/pages/cod-reductions/cod-reduction-form-page'))
const CodReductionDetailPage = lazy(() => import('@/pages/cod-reductions/cod-reduction-detail-page'))
const SalesChannelsPage = lazy(() => import('@/pages/sales-channels'))
const ReportsPage = lazy(() => import('@/pages/reports'))
const PancakePage = lazy(() => import('@/pages/pancake'))
const ReportRunPage = lazy(() => import('@/pages/report-run'))
const ReportBuilderPage = lazy(() => import('@/pages/report-builder'))
const ReportSavedDetailPage = lazy(() => import('@/pages/report-saved-detail'))
const ReportLeadTimePage = lazy(() => import('@/pages/report-lead-time'))
const ReportProvinceChannelPage = lazy(
  () => import('@/pages/report-province-channel'),
)
const ReportCostBySourcePage = lazy(
  () => import('@/pages/report-cost-by-source'),
)
const ReportGrossProfitBySourcePage = lazy(
  () => import('@/pages/report-gross-profit-by-source'),
)
const ReportDepositsPage = lazy(() => import('@/pages/report-deposits'))
const ReportDebtPage = lazy(() => import('@/pages/report-debt'))
const LeadsPage = lazy(() => import('@/pages/leads'))
const LeadNewPage = lazy(() => import('@/pages/lead-new'))
const LeadDetailPage = lazy(() => import('@/pages/lead-detail'))
const LeadEditPage = lazy(() => import('@/pages/lead-edit'))
const KpiPage = lazy(() => import('@/pages/kpi'))
const ReportLeadUserPage = lazy(() => import('@/pages/report-lead-user'))
const ReportKpiUserPage = lazy(() => import('@/pages/report-kpi-user'))
const ReportKpiDepartmentPage = lazy(() => import('@/pages/report-kpi-department'))
const ReportProductionPage = lazy(() => import('@/pages/report-production'))
const ReportSlowProductionPage = lazy(() => import('@/pages/report-slow-production'))
const ReportPieceworkPage = lazy(() => import('@/pages/report-piecework'))
const PieceworkLayout = lazy(() => import('@/pages/piecework/piecework-layout'))
const PieceworkOrdersPage = lazy(() => import('@/pages/piecework/piecework-orders-page'))
const PieceworkOrderPage = lazy(() => import('@/pages/piecework/piecework-order-page'))
const PieceworkMePage = lazy(() => import('@/pages/piecework/piecework-me-page'))
const PieceworkReviewPage = lazy(() => import('@/pages/piecework/piecework-review-page'))
const PieceworkPeriodsPage = lazy(() => import('@/pages/piecework/piecework-periods-page'))
const PieceworkConfigPage = lazy(() => import('@/pages/piecework/piecework-config-page'))
const ReportProductSizesPage = lazy(() => import('@/pages/report-product-sizes'))
const ReportSourceSizesPage = lazy(() => import('@/pages/report-source-sizes'))
const SettingsLeadRecallPage = lazy(() => import('@/pages/settings/lead-recall'))
const SettingsIndexPage = lazy(() => import('@/pages/settings/index'))
const SettingsUsersPage = lazy(() => import('@/pages/settings/users'))
const SettingsFrameColorsPage = lazy(() => import('@/pages/settings/frame-colors'))
const SettingsOrderTagsPage = lazy(() => import('@/pages/settings/order-tags'))
const SettingsOrderSourcesPage = lazy(() => import('@/pages/settings/order-sources'))
const SettingsCustomerSourcesPage = lazy(() => import('@/pages/settings/customer-sources'))
const SettingsLeadCustomerTypesPage = lazy(
  () => import('@/pages/settings/lead-customer-types'),
)
const SettingsShopInfoPage = lazy(() => import('@/pages/settings/shop-info'))
const SettingsOrderLockPage = lazy(() => import('@/pages/settings/order-lock'))
const SettingsDepartmentsPage = lazy(() => import('@/pages/settings/departments'))
const SettingsBranchesPage = lazy(() => import('@/pages/settings/branches'))
const SettingsPositionsPage = lazy(() => import('@/pages/settings/positions'))
const SettingsPositionEditorPage = lazy(
  () => import('@/pages/settings/position-editor'),
)
const SettingsPermissionMatrixPage = lazy(
  () => import('@/pages/settings/permission-matrix'),
)
const SettingsShippingPage = lazy(() => import('@/pages/settings/shipping'))
const SettingsMaterialCostsPage = lazy(
  () => import('@/pages/settings/material-costs'),
)
const LoginPage = lazy(() => import('@/pages/login'))
const SetupPage = lazy(() => import('@/pages/setup'))
const NotFoundPage = lazy(() => import('@/pages/not-found'))
const ForbiddenPage = lazy(() => import('@/pages/forbidden'))
const OrderInvoicePrintPage = lazy(() => import('@/pages/print/order-invoice'))

export const router = createBrowserRouter([
  {
    // Route gốc KHÔNG path — chỉ để đặt errorElement bao mọi route con. Lỗi tải
    // chunk sau deploy (hash đổi) hoặc lỗi render đều nổi lên đây → RootErrorBoundary
    // tự tải lại (bản mới) thay vì hiện màn hình lỗi mặc định của react-router.
    errorElement: <RootErrorBoundary />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
      },
  {
    path: '/setup',
    element: <SetupPage />,
  },
  // Print route — require login nhưng KHÔNG dùng AppShell (không sidebar/topbar)
  // để layout sạch cho in PDF. Mở qua window.open() từ trang detail.
  // Phiếu giao thì dùng API hãng VC qua openShipmentLabel(), không cần route.
  // Permission gate: chỉ user có ORDER_PRINT mới in được hoá đơn / báo giá.
  {
    path: '/print/orders/:id',
    element: (
      <ProtectedRoute>
        <PermissionGate permission={PERMISSIONS.ORDER_PRINT}>
          <OrderInvoicePrintPage />
        </PermissionGate>
      </ProtectedRoute>
    ),
  },
  {
    // Phiếu chi (voucher) — ngoài AppShell, render từ snapshot bất biến để in.
    path: '/print/expenses/:ids',
    element: (
      <ProtectedRoute>
        <PermissionGate permission={[PERMISSIONS.EXPENSE_VIEW, PERMISSIONS.EXPENSE_REQUEST]} mode="any">
          <ExpenseVoucherPrintPage />
        </PermissionGate>
      </ProtectedRoute>
    ),
  },
  {
    // Mở 1 file chứng từ từ link trong file Excel tab Phiếu chi — ngoài AppShell. KHÔNG gate quyền ở
    // route: RLS của expense_attachments quyết định, người duyệt theo vị trí / người không cần expense:view.
    path: '/expenses/files/:id',
    element: (
      <ProtectedRoute>
        <ExpenseFilePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/print/expenses-report',
    element: (
      <ProtectedRoute>
        <PermissionGate permission={PERMISSIONS.EXPENSE_REPORT_VIEW}>
          <ExpenseReportPrintPage />
        </PermissionGate>
      </ProtectedRoute>
    ),
  },
  {
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      // Dashboard — landing page. Mọi authenticated user vào được; KPI cards
      // bên trong tự ẩn theo permission.
      { index: true, element: <DashboardPage /> },
      // Đơn hàng list/detail — gate ORDER_VIEW. Nút edit trong page tự gate.
      {
        path: 'orders',
        element: (
          <PermissionGate permission={PERMISSIONS.ORDER_VIEW}>
            <OrdersPage />
          </PermissionGate>
        ),
      },
      {
        path: 'orders/new',
        element: (
          <PermissionGate permission={PERMISSIONS.ORDER_CREATE}>
            <OrderNewPage />
          </PermissionGate>
        ),
      },
      {
        path: 'orders/:id',
        element: (
          <PermissionGate permission={PERMISSIONS.ORDER_VIEW}>
            <OrderDetailPage />
          </PermissionGate>
        ),
      },
      {
        path: 'orders/:id/edit',
        element: (
          <PermissionGate permission={PERMISSIONS.ORDER_EDIT}>
            <OrderEditPage />
          </PermissionGate>
        ),
      },
      // Khách tiềm năng (lead) — port từ aloha-dms. Gate lead:view; các nút
      // bên trong tự gate create/edit/assign/convert.
      {
        path: 'leads',
        element: (
          <PermissionGate permission={PERMISSIONS.LEAD_VIEW}>
            <LeadsPage />
          </PermissionGate>
        ),
      },
      {
        path: 'leads/new',
        element: (
          <PermissionGate permission={PERMISSIONS.LEAD_CREATE}>
            <LeadNewPage />
          </PermissionGate>
        ),
      },
      {
        path: 'leads/:id',
        element: (
          <PermissionGate permission={PERMISSIONS.LEAD_VIEW}>
            <LeadDetailPage />
          </PermissionGate>
        ),
      },
      {
        path: 'leads/:id/edit',
        element: (
          <PermissionGate permission={PERMISSIONS.LEAD_EDIT}>
            <LeadEditPage />
          </PermissionGate>
        ),
      },
      {
        path: 'kpi',
        element: (
          <PermissionGate permission={PERMISSIONS.KPI_VIEW}>
            <KpiPage />
          </PermissionGate>
        ),
      },
      {
        path: 'customers',
        element: (
          <PermissionGate permission={PERMISSIONS.CUSTOMER_VIEW}>
            <CustomersPage />
          </PermissionGate>
        ),
      },
      {
        path: 'customers/:id',
        element: (
          <PermissionGate permission={PERMISSIONS.CUSTOMER_VIEW}>
            <CustomerDetailPage />
          </PermissionGate>
        ),
      },
      {
        path: 'products',
        element: (
          <PermissionGate permission={PERMISSIONS.PRODUCT_VIEW}>
            <ProductsPage />
          </PermissionGate>
        ),
      },
      {
        path: 'products/new',
        element: (
          <PermissionGate permission={PERMISSIONS.PRODUCT_CREATE}>
            <ProductNewPage />
          </PermissionGate>
        ),
      },
      {
        path: 'products/:id/edit',
        element: (
          <PermissionGate permission={PERMISSIONS.PRODUCT_EDIT}>
            <ProductEditPage />
          </PermissionGate>
        ),
      },
      {
        path: 'products/keywords',
        element: (
          <PermissionGate permission={PERMISSIONS.PRODUCT_KEYWORD_MANAGE}>
            <ProductKeywordsPage />
          </PermissionGate>
        ),
      },
      {
        path: 'materials',
        element: (
          <PermissionGate permission={PERMISSIONS.MATERIAL_VIEW}>
            <MaterialsPage />
          </PermissionGate>
        ),
      },
      {
        path: 'materials/:id/edit',
        element: (
          <PermissionGate permission={PERMISSIONS.MATERIAL_EDIT}>
            <MaterialEditPage />
          </PermissionGate>
        ),
      },
      {
        path: 'btp/report',
        element: (
          <PermissionGate permission={PERMISSIONS.BTP_VIEW}>
            <BtpReportPage />
          </PermissionGate>
        ),
      },
      {
        // Lịch sử lệnh SX chỉ ĐỌC → gate btp:view, không đòi btp:produce (quản lý
        // xem được lịch sử mà không cần quyền tạo lệnh).
        path: 'btp/productions',
        element: (
          <PermissionGate permission={PERMISSIONS.BTP_VIEW}>
            <BtpProductionsPage />
          </PermissionGate>
        ),
      },
      {
        // Phiếu sản xuất thành phẩm làm sẵn. XEM gate btp:view (phiếu không có
        // một con số tiền nào); LẬP/SỬA gate btp:prebuilt_manage ngay trong trang
        // form — route chỉ cần chặn người không được xem module BTP.
        path: 'btp/prebuilt',
        element: (
          <PermissionGate permission={PERMISSIONS.BTP_VIEW}>
            <BtpPrebuiltListPage />
          </PermissionGate>
        ),
      },
      {
        path: 'btp/prebuilt/new',
        element: (
          <PermissionGate permission={PERMISSIONS.BTP_PREBUILT_MANAGE}>
            <BtpPrebuiltFormPage />
          </PermissionGate>
        ),
      },
      {
        path: 'btp/prebuilt/:id/edit',
        element: (
          <PermissionGate permission={PERMISSIONS.BTP_PREBUILT_MANAGE}>
            <BtpPrebuiltFormPage />
          </PermissionGate>
        ),
      },
      {
        // ĐẶT SAU 'new': react-router khớp theo thứ tự khai, để :id trước thì
        // /btp/prebuilt/new rơi vào trang chi tiết và đi tra phiếu số "new".
        path: 'btp/prebuilt/:id',
        element: (
          <PermissionGate permission={PERMISSIONS.BTP_VIEW}>
            <BtpPrebuiltDetailPage />
          </PermissionGate>
        ),
      },
      {
        // Đặt TRƯỚC 'btp' không cần thiết (không có :id) nhưng giữ cạnh nhau cho dễ đọc.
        path: 'btp/produce',
        element: (
          <PermissionGate permission={PERMISSIONS.BTP_PRODUCE}>
            <BtpProducePage />
          </PermissionGate>
        ),
      },
      {
        path: 'btp',
        element: (
          <PermissionGate permission={PERMISSIONS.BTP_VIEW}>
            <BtpItemsPage />
          </PermissionGate>
        ),
      },
      {
        path: 'warehouse',
        element: (
          <PermissionGate permission={PERMISSIONS.WH_MATERIAL_VIEW}>
            <WarehouseLayout />
          </PermissionGate>
        ),
        children: [
          { index: true, element: <WhMaterialsPage /> },
          { path: 'history', element: <WhHistoryPage /> },
          { path: 'stock-in', element: <WhStockInHistoryPage /> },
          { path: 'optimize', element: <WhOptimizePage /> },
          { path: 'report', element: <WhCutReportPage /> },
        ],
      },
      {
        // Đặt TRƯỚC 'warehouse/:id' để 'batch' không bị nuốt thành :id.
        path: 'warehouse/batch/:batchId',
        element: (
          <PermissionGate permission={PERMISSIONS.WH_MATERIAL_VIEW}>
            <WhBatchDetailPage />
          </PermissionGate>
        ),
      },
      {
        path: 'warehouse/:id',
        element: (
          <PermissionGate permission={PERMISSIONS.WH_MATERIAL_VIEW}>
            <WhMaterialDetailPage />
          </PermissionGate>
        ),
      },
      {
        path: 'purchases',
        element: (
          <PermissionGate permission={PERMISSIONS.PURCHASE_VIEW}>
            <PurchasesPage />
          </PermissionGate>
        ),
      },
      {
        path: 'purchases/new',
        element: (
          <PermissionGate permission={PERMISSIONS.PURCHASE_CREATE}>
            <PurchaseNewPage />
          </PermissionGate>
        ),
      },
      {
        path: 'purchases/:id',
        element: (
          <PermissionGate permission={PERMISSIONS.PURCHASE_VIEW}>
            <PurchaseDetailPage />
          </PermissionGate>
        ),
      },
      {
        path: 'suppliers',
        element: (
          <PermissionGate permission={PERMISSIONS.SUPPLIER_VIEW}>
            <SuppliersPage />
          </PermissionGate>
        ),
      },
      {
        path: 'finance',
        element: (
          <PermissionGate permission={PERMISSIONS.FINANCE_VIEW}>
            <FinancePage />
          </PermissionGate>
        ),
      },
      {
        path: 'expenses',
        element: (
          <PermissionGate permission={[PERMISSIONS.EXPENSE_VIEW, PERMISSIONS.EXPENSE_REQUEST]} mode="any">
            <ExpensesLayout />
          </PermissionGate>
        ),
        children: [
          { index: true, element: <ExpensesListPage /> },
          {
            path: 'overview',
            element: (
              <PermissionGate permission={[PERMISSIONS.EXPENSE_VIEW, PERMISSIONS.EXPENSE_REQUEST]} mode="any">
                <ExpensesOverviewPage />
              </PermissionGate>
            ),
          },
          {
            path: 'org',
            element: (
              <PermissionGate permission={[PERMISSIONS.EXPENSE_VIEW, PERMISSIONS.EXPENSE_REQUEST]} mode="any">
                <ExpensesOrgPage />
              </PermissionGate>
            ),
          },
          {
            path: 'audit',
            element: (
              <PermissionGate permission={PERMISSIONS.EXPENSE_VIEW}>
                <ExpensesAuditPage />
              </PermissionGate>
            ),
          },
          {
            path: 'inbox',
            element: (
              <PermissionGate
                permission={[PERMISSIONS.EXPENSE_APPROVE, PERMISSIONS.EXPENSE_CREATE_COMMAND, ...EXPENSE_FUND_PERMISSIONS]}
                mode="any"
              >
                <ExpensesInboxPage />
              </PermissionGate>
            ),
          },
          {
            path: 'report',
            element: (
              <PermissionGate permission={PERMISSIONS.EXPENSE_REPORT_VIEW}>
                <ExpensesReportPage />
              </PermissionGate>
            ),
          },
          {
            path: 'flow',
            element: (
              <PermissionGate permission={PERMISSIONS.EXPENSE_FLOW_MANAGE}>
                <ExpensesFlowPage />
              </PermissionGate>
            ),
          },
          {
            path: 'types',
            element: (
              <PermissionGate permission={PERMISSIONS.EXPENSE_TYPE_MANAGE}>
                <ExpensesTypesPage />
              </PermissionGate>
            ),
          },
          {
            path: 'deposits',
            element: (
              <PermissionGate permission={[PERMISSIONS.DEPOSIT_VIEW, PERMISSIONS.DEPOSIT_CREATE]} mode="any">
                <DepositsListPage />
              </PermissionGate>
            ),
          },
        ],
      },
      {
        path: 'expenses/new',
        element: (
          <PermissionGate permission={PERMISSIONS.EXPENSE_REQUEST}>
            <ExpenseCreatePage />
          </PermissionGate>
        ),
      },
      {
        path: 'expenses/:id',
        element: (
          <PermissionGate permission={[PERMISSIONS.EXPENSE_VIEW, PERMISSIONS.EXPENSE_REQUEST]} mode="any">
            <ExpenseDetailPage />
          </PermissionGate>
        ),
      },
      {
        path: 'expenses/:id/edit',
        element: (
          <PermissionGate permission={PERMISSIONS.EXPENSE_REQUEST}>
            <ExpenseEditPage />
          </PermissionGate>
        ),
      },
      {
        path: 'expenses/deposits/new',
        element: (
          <PermissionGate permission={PERMISSIONS.DEPOSIT_CREATE}>
            <DepositCreatePage />
          </PermissionGate>
        ),
      },
      {
        path: 'expenses/deposits/:id',
        element: (
          <PermissionGate permission={[PERMISSIONS.DEPOSIT_VIEW, PERMISSIONS.DEPOSIT_CREATE]} mode="any">
            <DepositDetailPage />
          </PermissionGate>
        ),
      },
      // Hạ COD (migration 20261026_0002). Danh sách + chi tiết KHÔNG gate quyền: người duyệt
      // theo NGƯỜI / VỊ TRÍ / sale phụ trách không cần quyền gì (kể cả order:view) mà vẫn phải
      // mở được đề nghị chờ mình — chặn ở đây là đề nghị kẹt. RLS chỉ trả đề nghị họ được xem.
      {
        path: 'cod-reductions',
        element: <CodReductionsLayout />,
        children: [
          { index: true, element: <CodReductionsListPage key="all" /> },
          { path: 'inbox', element: <CodReductionsListPage key="inbox" mode="inbox" /> },
          {
            path: 'report',
            element: (
              <PermissionGate permission={PERMISSIONS.COD_REDUCTION_REPORT_VIEW}>
                <CodReductionsReportPage />
              </PermissionGate>
            ),
          },
          {
            path: 'flow',
            element: (
              <PermissionGate permission={PERMISSIONS.COD_REDUCTION_FLOW_MANAGE}>
                <CodReductionsFlowPage />
              </PermissionGate>
            ),
          },
          {
            path: 'reasons',
            element: (
              <PermissionGate permission={PERMISSIONS.COD_REDUCTION_REASON_MANAGE}>
                <CodReductionsReasonsPage />
              </PermissionGate>
            ),
          },
        ],
      },
      {
        path: 'cod-reductions/new',
        element: (
          <PermissionGate permission={PERMISSIONS.COD_REDUCTION_REQUEST}>
            <CodReductionFormPage />
          </PermissionGate>
        ),
      },
      {
        path: 'cod-reductions/:id',
        element: <CodReductionDetailPage />,
      },
      {
        path: 'cod-reductions/:id/edit',
        element: (
          <PermissionGate permission={PERMISSIONS.COD_REDUCTION_REQUEST}>
            <CodReductionFormPage />
          </PermissionGate>
        ),
      },
      // Khoán (migration 20261102_0001..0003). Khung gate BẤT KỲ quyền module; từng tab
      // gate CÙNG điều kiện với use-piecework-tabs.ts. Trang đầu tự chuyển người chỉ có
      // quyền cấu hình / chốt kỳ sang tab của họ (không 403).
      {
        path: 'piecework',
        element: (
          <PermissionGate mode="any" permission={PIECEWORK_MODULE_PERMISSIONS}>
            <PieceworkLayout />
          </PermissionGate>
        ),
        children: [
          { index: true, element: <PieceworkOrdersPage /> },
          {
            path: 'orders/:orderId',
            element: (
              <PermissionGate mode="any" permission={[PERMISSIONS.PIECEWORK_DECLARE, PERMISSIONS.PIECEWORK_APPROVE]}>
                <PieceworkOrderPage />
              </PermissionGate>
            ),
          },
          {
            path: 'me',
            element: (
              <PermissionGate permission={PERMISSIONS.PIECEWORK_DECLARE}>
                <PieceworkMePage />
              </PermissionGate>
            ),
          },
          {
            path: 'review',
            element: (
              <PermissionGate permission={PERMISSIONS.PIECEWORK_APPROVE}>
                <PieceworkReviewPage />
              </PermissionGate>
            ),
          },
          {
            path: 'periods',
            element: (
              <PermissionGate mode="any" permission={[PERMISSIONS.PIECEWORK_APPROVE, PERMISSIONS.PIECEWORK_CLOSE_PERIOD]}>
                <PieceworkPeriodsPage />
              </PermissionGate>
            ),
          },
          {
            path: 'config',
            element: (
              <PermissionGate permission={PERMISSIONS.PIECEWORK_CONFIG}>
                <PieceworkConfigPage />
              </PermissionGate>
            ),
          },
        ],
      },
      {
        path: 'sales-channels',
        element: (
          <PermissionGate permission={PERMISSIONS.SALES_CHANNEL_VIEW}>
            <SalesChannelsPage />
          </PermissionGate>
        ),
      },
      // Reports module — gate bằng ANY of REPORTS_HUB_PERMISSIONS (chung với menu
      // sidebar). Trong page từng thẻ tự gate theo quyền riêng; template tự lọc
      // theo permission (filterMetricsByPermission ở report-catalog.ts).
      {
        path: 'reports',
        element: (
          <PermissionGate mode="any" permission={REPORTS_HUB_PERMISSIONS}>
            <ReportsPage />
          </PermissionGate>
        ),
      },
      {
        path: 'pancake',
        element: (
          <PermissionGate permission={PERMISSIONS.PANCAKE_VIEW}>
            <PancakePage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/builder',
        element: (
          <PermissionGate
            mode="any"
            permission={[
              PERMISSIONS.REPORT_DASHBOARD,
              PERMISSIONS.REPORT_REVENUE,
              PERMISSIONS.REPORT_PROFIT,
              PERMISSIONS.REPORT_BY_USER,
            ]}
          >
            <ReportBuilderPage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/run',
        element: (
          <PermissionGate
            mode="any"
            permission={[
              PERMISSIONS.REPORT_DASHBOARD,
              PERMISSIONS.REPORT_REVENUE,
              PERMISSIONS.REPORT_PROFIT,
              PERMISSIONS.REPORT_BY_USER,
            ]}
          >
            <ReportRunPage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/saved/:id',
        element: (
          <PermissionGate
            mode="any"
            permission={[
              PERMISSIONS.REPORT_DASHBOARD,
              PERMISSIONS.REPORT_REVENUE,
              PERMISSIONS.REPORT_PROFIT,
              PERMISSIONS.REPORT_BY_USER,
            ]}
          >
            <ReportSavedDetailPage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/lead-time',
        element: (
          <PermissionGate permission={PERMISSIONS.REPORT_DASHBOARD}>
            <ReportLeadTimePage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/province-channel',
        element: (
          <PermissionGate permission={PERMISSIONS.REPORT_DASHBOARD}>
            <ReportProvinceChannelPage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/cost-by-source',
        element: (
          <PermissionGate permission={PERMISSIONS.REPORT_DASHBOARD}>
            <ReportCostBySourcePage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/gross-profit-by-source',
        element: (
          <PermissionGate permission={PERMISSIONS.REPORT_PROFIT}>
            <ReportGrossProfitBySourcePage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/deposits',
        element: (
          <PermissionGate permission={PERMISSIONS.REPORT_DASHBOARD}>
            <ReportDepositsPage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/debt',
        element: (
          <PermissionGate permission={PERMISSIONS.REPORT_DASHBOARD}>
            <ReportDebtPage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/lead-user',
        element: (
          <PermissionGate permission={PERMISSIONS.REPORT_LEAD_USER}>
            <ReportLeadUserPage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/kpi-user',
        element: (
          <PermissionGate permission={PERMISSIONS.REPORT_KPI_USER}>
            <ReportKpiUserPage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/kpi-department',
        element: (
          <PermissionGate permission={PERMISSIONS.REPORT_KPI_DEPARTMENT}>
            <ReportKpiDepartmentPage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/production',
        element: (
          <PermissionGate permission={PERMISSIONS.REPORT_PRODUCTION}>
            <ReportProductionPage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/slow-production',
        element: (
          <PermissionGate permission={PERMISSIONS.REPORT_SLOW_PRODUCTION}>
            <ReportSlowProductionPage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/piecework',
        element: (
          <PermissionGate permission={PERMISSIONS.REPORT_PIECEWORK}>
            <ReportPieceworkPage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/product-sizes',
        element: (
          <PermissionGate permission={PERMISSIONS.REPORT_PRODUCT_SIZE}>
            <ReportProductSizesPage />
          </PermissionGate>
        ),
      },
      {
        path: 'reports/source-sizes',
        element: (
          <PermissionGate permission={PERMISSIONS.REPORT_PRODUCT_SIZE}>
            <ReportSourceSizesPage />
          </PermissionGate>
        ),
      },

      {
        path: 'settings',
        children: [
          { index: true, element: <SettingsIndexPage /> },
          {
            path: 'users',
            element: (
              <PermissionGate permission={PERMISSIONS.USER_MANAGE}>
                <SettingsUsersPage />
              </PermissionGate>
            ),
          },
          {
            path: 'frame-colors',
            element: (
              <PermissionGate permission={PERMISSIONS.FRAME_COLOR_MANAGE}>
                <SettingsFrameColorsPage />
              </PermissionGate>
            ),
          },
          {
            path: 'order-tags',
            element: (
              <PermissionGate permission={PERMISSIONS.TAG_MANAGE}>
                <SettingsOrderTagsPage />
              </PermissionGate>
            ),
          },
          {
            path: 'order-sources',
            element: (
              <PermissionGate permission={PERMISSIONS.SOURCE_MANAGE}>
                <SettingsOrderSourcesPage />
              </PermissionGate>
            ),
          },
          {
            path: 'customer-sources',
            element: (
              <PermissionGate permission={PERMISSIONS.CUSTOMER_SOURCE_MANAGE}>
                <SettingsCustomerSourcesPage />
              </PermissionGate>
            ),
          },
          {
            path: 'lead-customer-types',
            element: (
              <PermissionGate permission={PERMISSIONS.LEAD_CUSTOMER_TYPE_MANAGE}>
                <SettingsLeadCustomerTypesPage />
              </PermissionGate>
            ),
          },
          {
            path: 'shop-info',
            element: (
              <PermissionGate permission={PERMISSIONS.SYSTEM_CONFIG}>
                <SettingsShopInfoPage />
              </PermissionGate>
            ),
          },
          {
            path: 'order-lock',
            element: (
              <PermissionGate permission={PERMISSIONS.SYSTEM_CONFIG}>
                <SettingsOrderLockPage />
              </PermissionGate>
            ),
          },
          {
            path: 'branches',
            element: (
              <PermissionGate permission={PERMISSIONS.DEPARTMENT_MANAGE}>
                <SettingsBranchesPage />
              </PermissionGate>
            ),
          },
          {
            path: 'departments',
            element: (
              <PermissionGate permission={PERMISSIONS.DEPARTMENT_MANAGE}>
                <SettingsDepartmentsPage />
              </PermissionGate>
            ),
          },
          {
            path: 'positions',
            element: (
              <PermissionGate permission={PERMISSIONS.POSITION_MANAGE}>
                <SettingsPositionsPage />
              </PermissionGate>
            ),
          },
          {
            path: 'positions/create',
            element: (
              <PermissionGate permission={PERMISSIONS.POSITION_MANAGE}>
                <SettingsPositionEditorPage />
              </PermissionGate>
            ),
          },
          {
            path: 'positions/:positionId',
            element: (
              <PermissionGate permission={PERMISSIONS.POSITION_MANAGE}>
                <SettingsPositionEditorPage />
              </PermissionGate>
            ),
          },
          {
            path: 'permission-matrix',
            element: (
              <PermissionGate permission={PERMISSIONS.ROLE_MANAGE}>
                <SettingsPermissionMatrixPage />
              </PermissionGate>
            ),
          },
          {
            path: 'lead-recall',
            element: (
              <PermissionGate permission={PERMISSIONS.LEAD_SETTING_MANAGE}>
                <SettingsLeadRecallPage />
              </PermissionGate>
            ),
          },
          {
            path: 'shipping',
            element: (
              <PermissionGate permission={PERMISSIONS.SHIPPING_CONFIG_API_KEY}>
                <SettingsShippingPage />
              </PermissionGate>
            ),
          },
          {
            path: 'material-costs',
            element: (
              <PermissionGate permission={PERMISSIONS.MATERIAL_COST_EDIT}>
                <SettingsMaterialCostsPage />
              </PermissionGate>
            ),
          },
        ],
      },
      { path: '403', element: <ForbiddenPage /> },
    ],
  },
  {
    path: '/404',
    element: <NotFoundPage />,
  },
  {
    path: '*',
    element: <Navigate to="/404" replace />,
  },
    ],
  },
])
